import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthAPI } from "../api";
import { useAuth } from "../context/AuthContext";

const NOTIFICATION_LABELS = {
  session_created: "New Session Booking",
  session_request: "New Session Request",
  session_approved: "Session Approved",
  session_cancelled: "Session Cancelled",
  session_rejected: "Session Rejected",
  session_countered: "New Time Proposed",
  session_completed: "Session Completed",
};

function Notifications() {
  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, isValidating } = useAuth();

  useEffect(() => {
    if (isValidating || !isAuthenticated) return;
    load();
  }, [isAuthenticated, isValidating]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setError(false);
    setLoading(true);
    try {
      const res = await AuthAPI.get("notifications/");
      setGroups(groupNotifications(res.data));
    } catch (err) {
      console.error("Fetch notifications error:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (notification) => {
    if (!notification.is_read) {
      try {
        await AuthAPI.patch(`notifications/${notification.id}/mark_read/`);
        setGroups((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((key) => {
            next[key] = next[key].map((item) =>
              item.id === notification.id ? { ...item, is_read: true } : item
            );
          });
          return next;
        });
      } catch (err) {
        console.error("Mark notification read error:", err);
      }
    }

    if (notification.session_id) {
      navigate(`/calendar?session_id=${notification.session_id}`);
    } else if (notification.request_id) {
      navigate(`/calendar?request_id=${notification.request_id}`);
    }
  };

  const allNotifications = useMemo(
    () => Object.values(groups).flat(),
    [groups]
  );
  const unreadCount = allNotifications.filter((item) => !item.is_read).length;
  const groupCount = Object.keys(groups).length;

  if (loading || isValidating) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <svg className="h-8 w-8 animate-spin text-[#282E6A]" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="rounded-[28px] border border-red-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600">
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008v.008H12v-.008zm8.25-3.75a8.25 8.25 0 11-16.5 0 8.25 8.25 0 0116.5 0z" />
            </svg>
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-[#111425]">Notifications did not load</h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-[#5d647f]">
            The inbox feed is unavailable right now. Retry before assuming there is nothing new.
          </p>
          <button
            onClick={load}
            className="mt-5 inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#1e2458]"
          >
            Retry inbox
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8 px-4 py-8 sm:px-6">
      <section className="overflow-hidden rounded-[30px] border border-[#dfe4f2] bg-white/85 shadow-[0_28px_70px_-48px_rgba(23,30,67,0.45)] backdrop-blur-sm">
        <div className="grid gap-6 px-6 py-7 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <div>
            <span className="inline-flex rounded-full border border-[#cfd5ee] bg-[#eef0fa] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#282E6A]">
              Notification inbox
            </span>
            <h1 className="mt-4 text-3xl leading-tight text-[#111425] sm:text-4xl">
              Stay on top of approvals, changes, and anything that needs your attention.
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-[#5d647f] sm:text-base">
              Session updates are grouped by request or booking, so the important context stays together instead of turning into scattered alerts.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={async () => {
                  try {
                    setMarkingAll(true);
                    await AuthAPI.patch("notifications/mark_all_read/");
                    setGroups((prev) => {
                      const next = {};
                      Object.entries(prev).forEach(([key, list]) => {
                        next[key] = list.map((item) => ({ ...item, is_read: true }));
                      });
                      return next;
                    });
                  } catch (err) {
                    console.error("Mark all read error:", err);
                  } finally {
                    setMarkingAll(false);
                  }
                }}
                className="inline-flex items-center justify-center rounded-xl bg-[#282E6A] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#1e2458] disabled:cursor-not-allowed disabled:bg-[#b6bad3]"
                disabled={markingAll || unreadCount === 0}
              >
                {markingAll ? "Marking…" : unreadCount === 0 ? "Inbox cleared" : "Mark all as read"}
              </button>
              <button
                onClick={load}
                className="inline-flex items-center justify-center rounded-xl border border-[#cfd5ee] bg-white px-5 py-2.5 text-sm font-medium text-[#282E6A] transition hover:bg-[#eef0fa]"
              >
                Refresh inbox
              </button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            {[
              { label: "Unread", value: unreadCount, tone: "bg-[#111425] text-white border-[#202640]" },
              { label: "Notification groups", value: groupCount, tone: "bg-[#eef0fa] text-[#111425] border-[#d9def3]" },
              { label: "Total alerts", value: allNotifications.length, tone: "bg-[#f5f1e7] text-[#111425] border-[#ead9b7]" },
            ].map((stat) => (
              <div key={stat.label} className={`rounded-[22px] border p-4 ${stat.tone}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] opacity-70">{stat.label}</p>
                <p className="mt-3 text-3xl font-semibold">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {groupCount === 0 ? (
        <section className="rounded-[28px] border border-[#dfe4f2] bg-white/92 px-6 py-14 text-center shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]">
          <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#eef0fa]">
            <svg className="h-8 w-8 text-[#282E6A]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
          </div>
          <p className="mt-5 text-xl font-semibold text-[#111425]">You're all caught up</p>
          <p className="mx-auto mt-2 max-w-lg text-sm leading-7 text-[#5d647f]">
            No new notifications right now. New requests, approvals, and schedule changes will appear here as they happen.
          </p>
        </section>
      ) : (
        Object.entries(groups).map(([key, items]) => {
          const unreadInGroup = items.filter((item) => !item.is_read).length;

          return (
            <section
              key={key}
              className="overflow-hidden rounded-[28px] border border-[#dfe4f2] bg-white/92 shadow-[0_22px_60px_-44px_rgba(23,30,67,0.35)]"
            >
              <div className="flex flex-col gap-3 border-b border-[#edf0f7] px-6 py-5 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7a8199]">
                    {key.startsWith("request-") ? "Request thread" : key.startsWith("session-") ? "Session thread" : "Updates"}
                  </p>
                  <h2 className="mt-2 text-xl font-semibold text-[#111425]">
                    {groupTitle(key, items[0])}
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-[#eef0fa] px-3 py-1 text-xs font-medium text-[#282E6A]">
                    {items.length} update{items.length === 1 ? "" : "s"}
                  </span>
                  {unreadInGroup > 0 && (
                    <span className="rounded-full bg-[#111425] px-3 py-1 text-xs font-medium text-white">
                      {unreadInGroup} unread
                    </span>
                  )}
                </div>
              </div>

              <div className="divide-y divide-[#edf0f7]">
                {items.map((notification) => (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => handleClick(notification)}
                    className={`flex w-full gap-4 px-6 py-5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#282E6A] ${
                      notification.is_read
                        ? "bg-white hover:bg-[#fafbfe]"
                        : "bg-[#f4f6ff] hover:bg-[#e8ecff]"
                    }`}
                  >
                    <div className="flex-shrink-0 pt-1">
                      <span className={`mt-1 block h-2.5 w-2.5 rounded-full ${notification.is_read ? "bg-[#d8dced]" : "bg-[#282E6A]"}`} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-[#111425]">
                            {NOTIFICATION_LABELS[notification.type] || "Notification"}
                          </p>
                          <p className="mt-1 text-sm leading-7 text-[#5d647f]">{notification.message}</p>
                        </div>
                        <p className="flex-shrink-0 text-xs font-medium uppercase tracking-[0.16em] text-[#8b92ab]">
                          {new Date(notification.created_at).toLocaleString([], {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}

function groupNotifications(notifications) {
  const map = {};
  notifications.forEach((notification) => {
    let key = "misc";
    if (notification.request_id) key = `request-${notification.request_id}`;
    else if (notification.session_id) key = `session-${notification.session_id}`;
    if (!map[key]) map[key] = [];
    map[key].push(notification);
  });
  Object.values(map).forEach((list) =>
    list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  );
  return map;
}

function groupTitle(key, notification) {
  if (key.startsWith("request-")) return `Session Request #${notification.request_id}`;
  if (key.startsWith("session-")) return `Session #${notification.session_id}`;
  return "Other";
}

export default Notifications;
